import { Box, HStack, IconButton, Text, Textarea, useDisclosure } from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useChatContextState, type ChatContext } from '../../../contexts/ChatContextProvider';
import type { ChatComposerPayload, ChatComposerPrefill } from './ChatComposer.types';
import { useToast } from '../../../hooks/ui/useToast';
import type { SearchResult } from '../../../services/search.service';
import { safeAnalytics } from '../../../utils/analytics';
import { ANALYTICS_EVENTS } from '../../../constants/analytics';
import { useVoiceDictation } from '../../../hooks/media/useVoiceDictation';
import { useIsReadOnly } from '../../../hooks/user/useWorkspaceRole';
import { useWorkspaceId } from '../../../contexts/WorkspaceContext';
import { chatDrafts } from '../../../stores/inMemoryDrafts';
import { MaterialIcon } from '../../ui/MaterialIcon';
import { ACTION_ROW_GLYPH_SIZE } from '../../ui/iconButtonMetrics';
import { COMPOSER_LABEL_CLASS } from './composerMetrics';
import { Tooltip } from '../../ui/Tooltip';
import { CommandPalette } from '../search/CommandPalette';
import AttachmentButton from './AttachmentButton';
import { DropOverlay } from '../../upload/intake/DropOverlay';
import { nameClipboardFile } from '../../upload/intake/nameClipboardFile';
import { ANY_FILE } from '../../upload/intake/rules';
import { useFileIntake, type FileIntake } from '../../upload/intake/useFileIntake';
import {
  describeRejections,
  MAX_ATTACHMENT_BYTES,
  MAX_ATTACHMENTS_PER_TURN,
  pickAttachments,
} from './composerFiles';
import { ComposerLiveButton } from './ComposerLiveButton';
import { ComposerPrimaryAction } from './ComposerPrimaryAction';
import { ComposerAttachmentTray } from './ComposerAttachmentTray';
import { ComposerAddContextButton, ComposerContextRow } from './ComposerContextRow';
import { ConnectorPickerButton } from './ConnectorPickerButton';
import { useSelectionDismissal } from './useContextSelection';
import { useComposerItemDrop } from './useComposerItemDrop';
import { BowerSpinner } from '../../ui/BowerSpinner';

// Pulse animation for recording state
const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.6; }
`;

/**
 * Below this container width the action row can't fit its labels, so the
 * labelled controls collapse to icon-only. ConnectorPickerButton participates
 * by putting `className={COMPOSER_LABEL_CLASS}` on its own label. The rule is
 * scoped to this container, so a control opts in by class alone.
 */
const NARROW_HIDES_LABEL = {
  '@container (max-width: 420px)': { [`& .${COMPOSER_LABEL_CLASS}`]: { display: 'none' } },
} as const;

/**
 * Ceiling on context entities per turn. Every attachment context costs the
 * agent two backend round-trips plus a download and up to ~10 MB of inline
 * data, on EVERY turn — the row is sticky across the conversation. The old
 * single-context composer bounded that structurally; this replaces the bound.
 */
const MAX_CONTEXT_ITEMS = 8;

/** The file drag's second line: the per-message limits, before anything is dropped. */
const ATTACH_LIMITS = `Up to ${MAX_ATTACHMENTS_PER_TURN}, ${Math.round(MAX_ATTACHMENT_BYTES / 1024 / 1024)} MB each`;

/**
 * Drag affordance for a workspace ITEM (added as context, not attached). A
 * file drag shows the shared `DropOverlay` instead. Overlays the composer
 * rather than displacing it: a panel that changes height mid-drag moves the
 * target out from under the cursor. `pointerEvents="none"` so it cannot
 * swallow the drop it is advertising.
 */
function ComposerContextOverlay() {
  return (
    <Box
      position="absolute"
      inset={0}
      zIndex={1}
      borderRadius="2xl"
      bg="bg.surface"
      opacity={0.95}
      pointerEvents="none"
      display="flex"
      alignItems="center"
      justifyContent="center"
      gap={2}
    >
      <MaterialIcon name="alternate_email" boxSize={5} color="brand.primary" />
      <Text fontSize="sm" fontWeight="medium" color="text.secondary">
        Drop to add as context
      </Text>
    </Box>
  );
}

/** The overlay for whatever is being dragged: an item (context) or files. */
function ComposerDragOverlay({
  isItemDragOver,
  intake,
}: {
  readonly isItemDragOver: boolean;
  readonly intake: FileIntake;
}) {
  if (isItemDragOver) return <ComposerContextOverlay />;
  return <DropOverlay intake={intake} compact hint={ATTACH_LIMITS} />;
}

/** The composer's border while something is dragged over it. */
function dragBorderColor(isItemDragOver: boolean, intake: FileIntake): string {
  return isItemDragOver || intake.drag.state !== 'idle' ? 'brand.primary' : 'border.default';
}

/** Which of the three routes a staged file arrived by. Reported to analytics. */
type AttachmentSource = 'button' | 'drop' | 'paste';

// Declared in ./ChatComposer.types so non-component code (birdChatEvents) can
// name them without an import cycle back through this module — see that file's
// header. Imported for local use below AND re-exported, so existing importers
// (BirdChatPanel, DashboardHero, the composer's own tests) are unaffected.
export type { ChatComposerPayload, ChatComposerPrefill } from './ChatComposer.types';

interface ChatComposerProps {
  /** Receives the full payload; the composer clears itself after calling. */
  readonly onSubmit: (payload: ChatComposerPayload) => void;
  /** Streaming state — disables input and turns send into stop. */
  readonly isStreaming?: boolean;
  /**
   * A turn this tab already asked to stop is still unwinding. Disables input
   * like `isStreaming`, but there is nothing more to stop, so send stays send,
   * disabled. A turn running on another request is `isStreaming`: Stop ends it
   * on the server (`composerTurnState`).
   */
  readonly isWaiting?: boolean;
  readonly onCancel?: () => void;
  /**
   * When provided, renders a labelled Live button beside send. Omitting it is
   * how a surface opts OUT of live — there is no separate visibility flag.
   * Today only the mobile Bower page wires it; the desktop panel and the
   * dashboard hero are send-only.
   */
  readonly onLiveTap?: () => void;
  /** Pre-fill the input (does NOT auto-send). */
  readonly prefill?: ChatComposerPrefill | null;
  /** Changing this key re-enables auto page context (panel passes sessionId). */
  readonly autoContextResetKey?: string;
  /**
   * Keeps the unsent draft (text and attached files) across unmounts under
   * this key, scoped to the active workspace: closing the Bower panel or
   * leaving the dashboard used to throw it away. One per surface, so pass a
   * constant ("panel", "hero"), never a session id. Absent keeps no draft.
   */
  readonly draftKey?: string;
  readonly placeholder?: string;
  /** "Bower can make mistakes" footer — on in the panel, off in the hero. */
  readonly showDisclaimer?: boolean;
  /**
   * Focus the textarea when the composer mounts. The Bower panel passes true
   * — it mounts fresh on every open and users start typing immediately;
   * without this their first keystrokes land on <body> and vanish. Leave
   * false (default) for always-mounted surfaces like the dashboard hero,
   * where stealing focus on page load would be wrong.
   */
  readonly autoFocusOnMount?: boolean;
  /**
   * Accessible name for the send button. The panel and the dashboard hero
   * render this same component, and two identical "Send message" buttons on
   * one page is an a11y/testing ambiguity — the hero overrides this.
   */
  readonly sendLabel?: string;
  /** Accessible name for the context `@`. Same rationale as `sendLabel`. */
  readonly addContextLabel?: string;
  /**
   * Accessible name for the textarea. MUST differ per surface, for the same
   * reason `sendLabel` does: the dashboard hero and the Bower panel can be on
   * screen together, and the placeholder — which is otherwise a textarea's
   * accessible name — is now deliberately IDENTICAL on both. Without distinct
   * labels the two textboxes are indistinguishable to screen readers and to any
   * role+name or placeholder selector (CR-029).
   */
  readonly composerLabel?: string;
  /** Accessible name for the sources picker, without the enabled-count suffix. */
  readonly sourcesLabel?: string;
}

/**
 * The composer's invitation to type. Exported so the panel, the dashboard hero
 * and the mobile page all read the SAME copy — they used to diverge, and a
 * user moving between surfaces got a different prompt each time. Callers should
 * not override `placeholder`; if a surface genuinely needs different wording,
 * change it here so every surface moves together.
 */
export const CHAT_COMPOSER_PLACEHOLDER = 'Ask Bower about your research…';

/**
 * The Bower chat composer — textarea + context chip + attachments +
 * connector picker + voice dictation + send/live buttons. THE single
 * input surface for Bower: rendered inside BirdChatPanel and as the
 * dashboard hero input, so styling and capabilities can never drift
 * between the two. Owns all transient input state; the parent decides
 * what "submit" means (send in-session vs open-the-panel-and-send).
 */
export function ChatComposer({
  onSubmit,
  isStreaming: isOwnTurnStreaming = false,
  isWaiting = false,
  onCancel,
  onLiveTap,
  prefill,
  autoContextResetKey,
  draftKey,
  // One placeholder, one job. The go-live entry point is a labelled control
  // in the action row, not a hint hidden inside the placeholder.
  placeholder = CHAT_COMPOSER_PLACEHOLDER,
  showDisclaimer = true,
  autoFocusOnMount = false,
  sendLabel = 'Send message',
  addContextLabel = 'Add context',
  // Defaults to the PANEL's name — the panel is the surface e2e targets, and
  // the hero overrides it.
  composerLabel = 'Message Bower',
  sourcesLabel,
}: ChatComposerProps) {
  // Everything that locks while a turn runs locks for a turn running elsewhere
  // too; only the primary action tells the two apart.
  const isStreaming = isOwnTurnStreaming || isWaiting;
  const toast = useToast();
  // Gates the two composer controls that would dead-end for a read-only role:
  // Live (blocked at the agent-service WebSocket handshake, routes/live.py) and
  // backend-fallback dictation (POSTs to /transcribe, which is write-gated and
  // 403s). The connector picker is NOT gated here — it only toggles which
  // read-only gateway tools are enabled per turn.
  //
  // Attachments are NOT gated here either, but not because they "never reach
  // the backend" — they do: agent_runner.py re-uploads every file to
  // POST /api/attachments/upload with the caller's own token, and that route
  // is write-gated (routes/index.ts). For a read-only role that upload is a
  // guaranteed 403, so the agent service now skips attempting it outright
  // (agent_runner.py's `_ATTACHMENT_UPLOAD_WRITER_ROLES` check) — nothing is
  // saved to the workspace. Gemini-native files (images, PDFs) are still
  // injected inline, so a guest attaching a PDF and asking "what does this
  // say" still gets a real answer; the model is told the file was read for
  // this turn only, not saved. The button stays visible because that
  // capability is real, not because uploads quietly succeed.
  //
  // A WRITER's files never ride the POST at all: `useAgentChat` uploads each
  // one (reserve, storage PUT, finalize) and sends artifact ids, so the agent
  // re-posts nothing. A read-only role keeps the multipart path above.
  const isReadOnly = useIsReadOnly();
  const workspaceId = useWorkspaceId();
  const draftId = draftKey && workspaceId ? `${workspaceId}:${draftKey}` : null;
  const [input, setInput] = useState(() => (draftId && chatDrafts.get(draftId)?.input) || '');
  const [attachedFiles, setAttachedFiles] = useState<File[]>(
    () => (draftId && chatDrafts.get(draftId)?.attachedFiles) || [],
  );
  // One draft per surface per workspace: the key carries no session, so it
  // changes only when the WORKSPACE does (the panel stays mounted across an SPA
  // switch), and any key change is a LOAD of that workspace's own draft. The
  // previous workspace's draft stays under its key, untouched. Keying by
  // session instead needed "move vs load" logic that a session id settling one
  // render after the workspace defeated, deleting both workspaces' drafts
  // (three review rounds on #3703; David chose per-workspace).
  const loadedDraftIdRef = useRef(draftId);
  const workspaceIdRef = useRef(workspaceId);
  workspaceIdRef.current = workspaceId;
  useEffect(() => {
    if (loadedDraftIdRef.current !== draftId) {
      loadedDraftIdRef.current = draftId;
      const next = draftId ? chatDrafts.get(draftId) : undefined;
      setInput(next?.input ?? '');
      setAttachedFiles(next?.attachedFiles ?? []);
      return;
    }
    if (!draftId) return;
    if (input || attachedFiles.length > 0) chatDrafts.set(draftId, { input, attachedFiles });
    else chatDrafts.delete(draftId);
  }, [draftId, input, attachedFiles]);
  const [manualContexts, setManualContexts] = useState<readonly ChatContext[]>([]);
  const [autoContextDisabled, setAutoContextDisabled] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { isOpen: isSearchOpen, onOpen: onSearchOpen, onClose: onSearchClose } = useDisclosure();

  // Auto-context from current page (set by page components via useChatContext hook)
  const { context: providerContext } = useChatContextState();

  // New session / workspace: re-enable auto-context AND drop the user's picks.
  // Clearing the picks used to happen for free, because the old effect
  // reassigned the single context slot from the provider on every change. With
  // context widened to an array that write path went away, and without this a
  // note picked in workspace A stays in the chip row and is sent as a context
  // id with the first message in workspace B (the panel stays mounted across
  // SPA workspace switches).
  useEffect(() => {
    setAutoContextDisabled(false);
    setManualContexts([]);
  }, [autoContextResetKey]);

  // The page's context, minus its selected cells while the researcher has
  // dismissed them (the chip's range pill). What the chip shows and what the
  // send carries both read this one wrapped `detail`.
  const { context: autoContext, dismissSelection } = useSelectionDismissal(
    autoContextDisabled ? null : (providerContext ?? null),
    autoContextResetKey,
  );

  /**
   * The page's own entity leads, then whatever the user picked. Derived
   * rather than stored so navigating between pages keeps the auto slot fresh
   * without clobbering the user's explicit picks.
   */
  const contexts = useMemo<readonly ChatContext[]>(() => {
    const merged = autoContext ? [autoContext, ...manualContexts] : manualContexts;
    const seen = new Set<string>();
    return merged.filter((c) => {
      const key = `${c.type}:${c.id}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [autoContext, manualContexts]);

  const handleRemoveContext = useCallback(
    (target: ChatContext) => {
      // Removing the page's auto context has to latch it off, or the next
      // render would put it straight back.
      if (autoContext && autoContext.type === target.type && autoContext.id === target.id) {
        setAutoContextDisabled(true);
      }
      setManualContexts((prev) =>
        prev.filter((c) => !(c.type === target.type && c.id === target.id)),
      );
    },
    [autoContext],
  );

  const handleDismissSelection = useCallback(
    (target: ChatContext) => {
      if (!autoContext || autoContext.type !== target.type || autoContext.id !== target.id) return;
      // ContextChip moves focus to the chip itself after this.
      dismissSelection();
      // No sheet name, no range: both describe the researcher's own data.
      safeAnalytics.track(ANALYTICS_EVENTS.CHAT_CONTEXT_SELECTION_DISMISSED, {
        entity_type: target.type,
      });
    },
    [autoContext, dismissSelection],
  );

  // Consume a prefill (empty-state prompt click, "Ask Bower" entry points).
  useEffect(() => {
    if (!prefill?.text) return;
    setInput(prefill.text);
    if (!prefill.keepContexts) setManualContexts(prefill.contexts ?? []);
    // Merged into the tray, not over it: a file dropped while the refused
    // send was in flight is still there. Handed-back files first, no repeats.
    const handedBack = prefill.files;
    if (handedBack) {
      setAttachedFiles((prev) =>
        [...handedBack, ...prev.filter((f) => !handedBack.includes(f))].slice(
          0,
          MAX_ATTACHMENTS_PER_TURN,
        ),
      );
    }
    const id = setTimeout(() => textareaRef.current?.focus(), 50);
    return () => clearTimeout(id);
  }, [prefill]);

  // Mount-time autofocus (Bower panel). Delayed a tick so the panel's width
  // transition has started and the element is focusable; matches the
  // prefill-focus pattern above. `autoFocusOnMount` is constant per surface,
  // so this runs once per mount in practice.
  useEffect(() => {
    if (!autoFocusOnMount) return;
    const id = setTimeout(() => textareaRef.current?.focus(), 50);
    return () => clearTimeout(id);
  }, [autoFocusOnMount]);

  // --- Voice dictation ---
  const [interimTranscript, setInterimTranscript] = useState('');

  // The workspace a dictation started in. A transcript that lands after a
  // workspace switch belongs to the workspace that was left: it is dropped,
  // never appended to the next workspace's draft (the switch prompt says the
  // dictation is interrupted, and it is).
  const dictationWorkspaceRef = useRef<string | null>(null);
  const handleDictationTranscript = useCallback((text: string) => {
    if (dictationWorkspaceRef.current !== workspaceIdRef.current) return;
    setInput((prev) => {
      if (prev.length > 0 && !/\s$/.test(prev)) {
        return prev + ' ' + text;
      }
      return prev + text;
    });
  }, []);

  const handleDictationError = useCallback(
    (message: string) => {
      toast({ title: 'Voice dictation', description: message, status: 'error' });
    },
    [toast],
  );

  const {
    isSupported: isDictationSupported,
    method: dictationMethod,
    isListening: isDictating,
    isTranscribing: isDictationTranscribing,
    startDictation,
    stopDictation,
    abortDictation,
  } = useVoiceDictation({
    onTranscript: handleDictationTranscript,
    onInterimTranscript: setInterimTranscript,
    onError: handleDictationError,
  });
  const beginDictation = useCallback(() => {
    dictationWorkspaceRef.current = workspaceId;
    startDictation();
  }, [workspaceId, startDictation]);
  // A workspace switch interrupts a dictation in flight: ABORT it, never stop
  // it. A stop finishes the take and posts the audio to /transcribe, and the
  // request resolves its workspace at send time, so the left workspace's audio
  // would cross the new workspace's PHI and trace gates and bill to it (review
  // d0e4ab4 on #3703). The transcript guard above stays as the backstop for a
  // request already in flight.
  const prevWorkspaceIdRef = useRef(workspaceId);
  useEffect(() => {
    if (prevWorkspaceIdRef.current === workspaceId) return;
    prevWorkspaceIdRef.current = workspaceId;
    // Unconditionally: a backend start still awaiting the microphone is not
    // "dictating" yet, and must not start in the new workspace.
    abortDictation();
    setInterimTranscript('');
  }, [workspaceId, abortDictation]);

  const handleSend = useCallback(() => {
    if (!input.trim() || isStreaming) return;
    if (isDictating) stopDictation();
    onSubmit({
      text: input.trim(),
      contexts,
      files: attachedFiles.length > 0 ? attachedFiles : undefined,
    });
    setInput('');
    setInterimTranscript('');
    setAttachedFiles([]);
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  }, [input, isStreaming, isDictating, stopDictation, onSubmit, contexts, attachedFiles]);

  const handleSearchResultClick = useCallback(
    (result: SearchResult) => {
      setManualContexts((prev) => {
        if (prev.some((c) => c.type === result.type && c.id === result.id)) return prev;
        if (prev.length + 1 >= MAX_CONTEXT_ITEMS) {
          // Tell the user rather than dropping the pick on the floor.
          toast({
            title: `You can attach up to ${MAX_CONTEXT_ITEMS} items`,
            description: 'Remove one to add another.',
            status: 'info',
          });
          if (prev.length + 1 > MAX_CONTEXT_ITEMS) return prev;
        }
        return [...prev, { type: result.type, id: result.id, name: result.title }];
      });
    },
    [toast],
  );

  // Workspace items dropped here become context chips, never attachments
  // (see useComposerItemDrop for how the two drop routes stay apart).
  const { isItemDragOver, handlers: itemDropHandlers } = useComposerItemDrop({
    manual: manualContexts,
    shown: contexts,
    max: MAX_CONTEXT_ITEMS,
    setManual: setManualContexts,
    toast,
  });

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
      return;
    }
    // `@` on an empty field is the keyboard route into the context picker.
    // Mid-sentence it stays a literal `@` — people write email addresses.
    if (e.key === '@' && input.length === 0) {
      e.preventDefault();
      onSearchOpen();
    }
  };

  // Auto-resize textarea
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
    const el = e.target;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 150)}px`;
  };

  // Auto-resize when dictation adds text
  useEffect(() => {
    if (textareaRef.current) {
      const el = textareaRef.current;
      el.style.height = 'auto';
      el.style.height = `${Math.min(el.scrollHeight, 150)}px`;
    }
  }, [input, interimTranscript]);

  /**
   * Stage already-gated files. `source` records WHICH of the three routes the
   * file came in by, so the drop and paste paths can be measured rather than
   * assumed — that is the whole reason for shipping them.
   */
  const stageAccepted = useCallback(
    (files: File[], source: AttachmentSource) => {
      if (files.length === 0) return;
      // Room is read from the rendered state, NOT captured inside the updater:
      // a `let` assigned in an updater is a side channel React is free to run
      // twice (StrictMode does), so the toast would fire on a double-invoke.
      // One user gesture is one render, so there is no staleness to chase here.
      const room = MAX_ATTACHMENTS_PER_TURN - attachedFiles.length;
      const admitted = room <= 0 ? [] : files.slice(0, room);
      if (admitted.length > 0) setAttachedFiles((prev) => [...prev, ...admitted]);
      if (files.length > admitted.length) {
        toast({
          title: `You can attach up to ${MAX_ATTACHMENTS_PER_TURN} files`,
          description: 'Send these first, then attach the rest.',
          status: 'info',
        });
      }
      // Nothing staged is not an attach. Without this guard the cap emits a
      // phantom `Chat File Attached` carrying file_count 0.
      if (admitted.length === 0) return;
      safeAnalytics.track(ANALYTICS_EVENTS.CHAT_FILE_ATTACHED, {
        file_count: admitted.length,
        file_type: admitted.length === 1 ? admitted[0].type : 'multiple',
        source,
        // How many the cap turned away. MAX_ATTACHMENTS_PER_TURN is a judgement
        // call (10), and without this the event cannot answer whether it ever
        // bites or whether the number is right — the admitted count alone looks
        // identical for "attached 10" and "wanted 400, got 10".
        capped_count: files.length - admitted.length,
      } as any);
    },
    [attachedFiles, toast],
  );

  /**
   * Say what was refused and why. Rejections previously went nowhere: the
   * button accepted an `onFilesRejected` callback that no caller ever passed,
   * so an oversize file simply vanished on pick (rule 12).
   */
  const reportRejections = useCallback(
    (errors: string[], anyAccepted: boolean) => {
      if (errors.length === 0) return;
      const refusedAll =
        errors.length === 1 ? "That file can't be attached" : "Those files can't be attached";
      toast({
        title: anyAccepted ? 'Some files were not attached' : refusedAll,
        // '; ' rather than '\n': the description reaches Chakra's
        // AlertDescription as a plain string with no `whiteSpace` override, so
        // newlines collapse and several reasons render as one run-on line.
        //
        // Bounded, because `errors` is not. MAX_ATTACHMENTS_PER_TURN bounds
        // what gets STAGED, but `pickAttachments` still runs over every file a
        // recursive directory drop produced, so a folder of 400 rejects built a
        // 14,688-character description and put 400 reasons on one line. The
        // count is what the user needs; the first few reasons are what makes it
        // actionable.
        description: describeRejections(errors),
        status: 'error',
      });
    },
    [toast],
  );

  /** Gate, then stage, then report. The drop and paste routes' single entry. */
  const handleIncomingFiles = useCallback(
    (files: readonly File[], source: AttachmentSource) => {
      const { accepted, errors } = pickAttachments(files);
      stageAccepted(accepted, source);
      reportRejections(errors, accepted.length > 0);
    },
    [stageAccepted, reportRejections],
  );

  const handleFilesSelected = useCallback(
    (files: File[]) => stageAccepted(files, 'button'),
    [stageAccepted],
  );

  const handleFilesRejected = useCallback(
    (_rejected: File[], errors: string[]) => reportRejections(errors, false),
    [reportRejections],
  );

  /**
   * Drop onto the composer, through the shared intake: its look (the compact
   * `DropOverlay`) and its live region. `pickAttachments` stays the one judge
   * of what may be attached (50 MB, research code by name, 10 per message),
   * so the intake takes ANY_FILE with no cap of its own and reports nothing.
   *
   * `dropOnly` because the attach button already owns the click and keyboard
   * routes; without it every click in the textarea would open a system dialog.
   * The textarea's own handlePaste is the paste route (it names clipboard
   * files first).
   *
   * NOT disabled mid-stream or mid-dictation, and that is load-bearing:
   * react-dropzone's `disabled` removes the root's drag handlers while
   * `rootRef` stays attached, so the document-level guard bails out inside
   * our own root and a drop navigated the tab to the file. The intake never
   * disables, and staging mid-stream is already how the attach button works;
   * send is what's gated.
   */
  const intake = useFileIntake({
    rule: ANY_FILE,
    surface: 'chat',
    maxFiles: Number.MAX_SAFE_INTEGER,
    maxBytes: Number.POSITIVE_INFINITY,
    dropOnly: true,
    selfReported: true,
    destinationLabel: 'this message',
    onFiles: (files) => handleIncomingFiles(files, 'drop'),
  });

  /**
   * Paste into the composer.
   *
   * Only intercepts when the clipboard actually carries files. Copying a
   * table out of a note puts text/html AND no files on the clipboard, so
   * preventing default unconditionally would break ordinary text paste, which
   * is the far more common act.
   */
  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
      const files = Array.from(e.clipboardData?.files ?? []);
      if (files.length === 0) return;
      e.preventDefault();
      handleIncomingFiles(
        files.map((file, index) => nameClipboardFile(file, index)),
        'paste',
      );
    },
    [handleIncomingFiles],
  );

  const handleRemoveFile = useCallback((index: number) => {
    setAttachedFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  // Dictation copy/iconography, derived once so each JSX slot carries a single
  // value instead of a three-way chain.
  const dictatingPlaceholder = dictationMethod === 'web_speech' ? 'Listening...' : 'Recording...';
  const idlePlaceholder = isDictationTranscribing ? 'Transcribing...' : placeholder;
  const composerPlaceholder = isDictating ? dictatingPlaceholder : idlePlaceholder;

  const transcribingTooltip = isDictationTranscribing ? 'Transcribing...' : 'Voice dictation';
  const activeDictationTooltip = isDictating ? 'Stop dictation' : transcribingTooltip;
  const dictationTooltip = isStreaming ? 'Wait for Bower to finish' : activeDictationTooltip;

  const activeDictationIcon = isDictating ? (
    <MaterialIcon name="stop_circle" boxSize={ACTION_ROW_GLYPH_SIZE} />
  ) : (
    <MaterialIcon name="mic" boxSize={ACTION_ROW_GLYPH_SIZE} />
  );
  const dictationIcon = isDictationTranscribing ? <BowerSpinner size="sm" /> : activeDictationIcon;

  return (
    <Box>
      <Box
        {...intake.getRootProps(itemDropHandlers)}
        // The dropzone root, and the reason a dropped file no longer navigates
        // the tab away. The browser's DEFAULT for a drop on a page that
        // handles none is to open the file — that is not an OS quirk and not
        // something a page can opt out of except by handling the event, which
        // is what getRootProps() does.
        position="relative"
        bg="bg.surface"
        borderRadius="2xl"
        border="1px solid"
        borderColor={dragBorderColor(isItemDragOver, intake)}
        _focusWithin={{ borderColor: 'brand.primary' }}
        transition="border-color 0.2s"
        p={3}
        // Container query, not a viewport breakpoint: the Bower panel is a
        // user-resizable pane (min 400px) inside a full-width viewport, so
        // {base,md} would report "desktop" while this box is 344px wide.
        // Below the threshold the opted-in labels are hidden and their
        // controls degrade to icons (each carries an aria-label and a
        // tooltip). The Live button deliberately opts OUT — see its own
        // comment — so the action row wraps instead of relying on this.
        sx={{ containerType: 'inline-size', ...NARROW_HIDES_LABEL }}
      >
        <ComposerDragOverlay isItemDragOver={isItemDragOver} intake={intake} />

        {/* Row 1 — context CHIPS: entities Bower may read. Renders only when
            something is selected; the `@` add control lives in the action row
            below, where it has a fixed home. See ComposerContextRow's header
            for why the two are deliberately apart — main's version of this
            comment still described the always-present row, which the button
            split replaced. */}
        <ComposerContextRow
          contexts={contexts}
          onRemove={handleRemoveContext}
          onDismissSelection={handleDismissSelection}
        />

        {/* Row 2 — attachments: files staged for this turn only. */}
        <ComposerAttachmentTray files={attachedFiles} onRemove={handleRemoveFile} />

        <CommandPalette
          isOpen={isSearchOpen}
          onClose={onSearchClose}
          mode="select"
          onSelectEntity={handleSearchResultClick}
        />

        {/* Row 3 — the ask. One placeholder, one job. The placeholder is now
          identical on every surface, so it can no longer serve as the accessible
          name: the hero and the panel co-mount on the dashboard and would expose
          two indistinguishable textboxes. `composerLabel` names each one. */}
        <Textarea
          aria-label={composerLabel}
          ref={textareaRef}
          value={interimTranscript ? `${input}${interimTranscript}` : input}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          placeholder={composerPlaceholder}
          size="sm"
          resize="none"
          rows={1}
          minH="36px"
          maxH="150px"
          border="none"
          bg="transparent"
          px={0}
          _focus={{ boxShadow: 'none' }}
          _focusVisible={{ boxShadow: 'none' }}
          isDisabled={isStreaming || isDictating || isDictationTranscribing}
        />

        {/* Row 4 — actions. Each control has exactly one job and a label.
            Wraps rather than overflows: the Live label deliberately never
            collapses, and every control here is either flexShrink={0} or
            minW-pinned, so at 320px (iPhone SE, and the WCAG 1.4.10 reflow
            target at 400% zoom) the row exceeds its 264px content box with
            nothing able to give. The panel's container clips rather than
            scrolls, so without wrapping the overflow silently cuts off Send. */}
        <HStack justify="space-between" mt={1} flexWrap="wrap" rowGap={1}>
          <HStack spacing={1}>
            {/* "Add context" and "Sources" as pills on one row (2026-09). */}
            <ComposerAddContextButton onAdd={onSearchOpen} addContextLabel={addContextLabel} />
            <AttachmentButton
              onFilesSelected={handleFilesSelected}
              onFilesRejected={handleFilesRejected}
            />
            {/* `@`, attach and Sources are one pill row (2026-09). The context CHIPS
            render above, and only when something is selected — see
            ComposerContextRow. An earlier comment here said there was no `@` in
            this row at all, which stopped being true when the button moved
            into it. */}
            <ConnectorPickerButton
              isDisabled={isStreaming || isDictating || isDictationTranscribing}
              sourcesLabel={sourcesLabel}
            />
          </HStack>
          <HStack spacing={2}>
            {/* Voice dictation button. The Web Speech path is browser-local and
                works fine for a read-only role; only the MediaRecorder->
                /transcribe fallback path is gated, since that endpoint is
                write-gated and would 403 for them. */}
            {isDictationSupported && !(isReadOnly && dictationMethod === 'backend') && (
              <Tooltip label={dictationTooltip} placement="top">
                <IconButton
                  aria-label={isDictating ? 'Stop dictation' : 'Voice dictation'}
                  icon={dictationIcon}
                  onClick={isDictating ? stopDictation : beginDictation}
                  size="md"
                  variant="ghost"
                  color={isDictating ? 'white' : 'text.secondary'}
                  bg={isDictating ? 'danger.500' : 'transparent'}
                  _hover={{
                    bg: isDictating ? 'danger.600' : 'bg.hover',
                  }}
                  borderRadius="lg"
                  isDisabled={isStreaming || isDictationTranscribing}
                  animation={isDictating ? `${pulse} 1.5s ease-in-out infinite` : undefined}
                  // Reduced motion drops the pulse but keeps the red fill —
                  // "recording" must stay legible without animation.
                  sx={{ '@media (prefers-reduced-motion: reduce)': { animation: 'none' } }}
                  minW="40px"
                  h="40px"
                />
              </Tooltip>
            )}
            {/* Live sits BESIDE send, not in its slot — a labelled control
                that's always there on surfaces that offer it, rather than an
                icon that only appears while the field happens to be empty.
                Hidden for read-only roles: the agent-service rejects the
                WebSocket handshake for them anyway (routes/live.py), so
                offering the control would be a dead end. */}
            {onLiveTap && !isReadOnly && (
              <ComposerLiveButton
                onLiveTap={onLiveTap}
                isDisabled={isStreaming || isDictating || isDictationTranscribing}
              />
            )}
            <ComposerPrimaryAction
              isStreaming={isOwnTurnStreaming}
              isWaiting={isWaiting}
              canSend={!!input.trim()}
              sendLabel={sendLabel}
              onCancel={onCancel}
              onSend={handleSend}
            />
          </HStack>
        </HStack>
      </Box>

      {showDisclaimer && (
        <Text fontSize="2xs" color="text.muted" textAlign="center" mt={2}>
          Bower can make mistakes. Check important info.
        </Text>
      )}
    </Box>
  );
}
